import { Box, Button, Container, FileUpload, Header, SpaceBetween, StatusIndicator } from '@amzn/awsui-components-core';
import React, { useEffect, useState } from 'react';
import { AppLogoService } from 'src/services/AppLogoService';

interface LogoLibraryUploadProps {
  onDismiss: () => void;
  onSuccess: () => void;
}

const LogoLibraryUploadModal: React.FC<LogoLibraryUploadProps> = ({ onDismiss, onSuccess }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (files.length > 0 && files[0].type === 'image/svg+xml') {
      const url = URL.createObjectURL(files[0]);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(null);
  }, [files]);

  const handleUpload = async () => {
    if (files.length === 0) return;

    setError(null);
    setIsUploading(true);

    const file = files[0];
    const validationError = AppLogoService.validateFile(file);
    if (validationError) {
      setError(validationError);
      setIsUploading(false);
      return;
    }

    const result = await AppLogoService.uploadSharedLogo(file);
    setIsUploading(false);

    if (result.success) {
      onSuccess();
    } else {
      setError(result.error || 'Upload failed.');
    }
  };

  return (
    <Container
      header={
        <Header
          variant="h2"
          actions={
            <SpaceBetween size="xs" direction="horizontal">
              <Button variant="link" onClick={onDismiss}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleUpload} loading={isUploading} disabled={files.length === 0}>
                Upload
              </Button>
            </SpaceBetween>
          }
        >
          Upload Shared Logo
        </Header>
      }
    >
      <SpaceBetween size="m">
        <FileUpload
          value={files}
          onChange={({ detail }) => {
            setFiles(detail.value);
            setError(null);
          }}
          accept=".svg,.png"
          constraintText="Accepted formats: SVG, PNG. Max size: 1 MB."
          i18nStrings={{
            uploadButtonText: () => 'Drag & Drop',
            dropzoneText: () => 'Drop file here to upload',
            removeFileAriaLabel: () => 'Remove file',
            errorIconAriaLabel: 'Error'
          }}
          showFileSize
          errorText={error || undefined}
        />
        {isUploading && <StatusIndicator type="loading">Uploading...</StatusIndicator>}
        {previewUrl && (
          <Box>
            <Box variant="awsui-key-label">Preview</Box>
            <div className="logo-library__upload-preview">
              <img src={previewUrl} alt="Preview" />
            </div>
          </Box>
        )}
      </SpaceBetween>
    </Container>
  );
};

export default LogoLibraryUploadModal;
